import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { rememberPendingAuthFlowType, supabase } from '../utils/supabaseClient';
import cookieStorageAdapter, { clearAuthCookie } from '../utils/cookieStorageAdapter';
import {
  savePreferencesToCookie,
  readPreferencesFromCookie,
  restorePreferencesToLocalStorage,
  clearPreferencesCookie,
  resetPreferencesToDefaults,
  emitAuthEvent,
  AUTH_EVENTS,
  isPreferenceKey,
} from '../utils/userPreferencesCookie';
import { getSafeAuthRedirect } from '../utils/authRedirect';
import {
  INTRO_SPLASH_V3_SEEN_AT_KEY,
  INTRO_SPLASH_V3_SEEN_KEY,
} from '../utils/introSplashV3Persistence';
import { getBillingState } from '../services/billingApi';

const awaitWithTimeout = async (promise, timeoutMs, label) => {
  let timeoutId;

  const wrapped = Promise.resolve(promise).then(
    (value) => ({ ok: true, value }),
    (error) => ({ ok: false, error })
  );

  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      resolve({
        ok: false,
        error: new Error(`[AuthContext] Timeout: ${label} (${timeoutMs}ms)`),
      });
    }, timeoutMs);
  });

  const result = await Promise.race([wrapped, timeout]);
  clearTimeout(timeoutId);

  if (!result.ok) {
    throw result.error;
  }

  return result.value;
};

const isAuthTimeoutError = (error) => {
  const message = error?.message || '';
  return typeof message === 'string' && message.includes('[AuthContext] Timeout:');
};

const PREFERENCES_VERSION = 1;
const PREFERENCES_TS_PREFIX = 'mpt_pref_ts__';
const AUTH_ACTION_LOADER_DELAY_MS = 350;
const MPT_MAINTENANCE_ADMIN_BYPASS_KEY = 'mpt_maintenance_admin_bypass';
const MPT_MAINTENANCE_ADMIN_BYPASS_TTL_MS = 12 * 60 * 60 * 1000;
const PREFERENCES_META_KEYS = new Set(['__version', '__ts']);
const PERSISTENT_DEVICE_MARKER_KEYS = new Set([
  INTRO_SPLASH_V3_SEEN_KEY,
  INTRO_SPLASH_V3_SEEN_AT_KEY,
]);
const SYNCED_PREFERENCE_KEYS = new Set([
  'theme',
  'highContrast',
  'showSnow',
  'i18nextLng',
  'language',
  'showLocalTime',
    'showInfoButtons',
  'hideNavLabels',
  'liquidGlassEnabled',
  'soundEnabled',
  'allParksFavorites',
  'favoritesInfo',
  'favoriteParkIds',
  'attractionSortPreference',
  'restaurantSortPreference',
  'historyDisplayTz',
  'showAsia',
  'hideImportantNotice',
  'hideImportantNoticeAsterix',
  'parkViewMode_DLP',
  'parkViewMode_WDS',
  INTRO_SPLASH_V3_SEEN_KEY,
  INTRO_SPLASH_V3_SEEN_AT_KEY,
]);

const clearSyncedPreferencesFromLocalStorage = (removeItemFn) => {
  const remove = typeof removeItemFn === 'function' ? removeItemFn : localStorage.removeItem;
  try {
    SYNCED_PREFERENCE_KEYS.forEach((key) => {
      if (PERSISTENT_DEVICE_MARKER_KEYS.has(key)) {
        return;
      }

      try {
        remove.call(localStorage, key);
      } catch {
        // ignore
      }
      try {
        remove.call(localStorage, `${PREFERENCES_TS_PREFIX}${key}`);
      } catch {
        // ignore
      }
    });

    // Clés dynamiques (elles sont aussi synchronisées)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;

      const isDynamicKey = k.startsWith('parkOrder_') || k.startsWith('newsBlock_');
      const isDynamicTsKey = k.startsWith(PREFERENCES_TS_PREFIX) && (
        k.slice(PREFERENCES_TS_PREFIX.length).startsWith('parkOrder_') ||
        k.slice(PREFERENCES_TS_PREFIX.length).startsWith('newsBlock_')
      );

      if (isDynamicKey || isDynamicTsKey) {
        try {
          remove.call(localStorage, k);
        } catch {
          // ignore
        }
      }
    }
  } catch {
    // ignore
  }
};

const isSyncedPreferenceKey = (key) => {
  if (!key || typeof key !== 'string') return false;
  if (key.startsWith(PREFERENCES_TS_PREFIX)) return false;
  if (key === '__ts' || key === '__version') return false;
  if (SYNCED_PREFERENCE_KEYS.has(key)) return true;
  if (key.startsWith('parkOrder_')) return true;
  if (key.startsWith('newsBlock_')) return true;
  return false;
};

const getLocalPreferenceTimestamp = (key) => {
  try {
    const raw = localStorage.getItem(`${PREFERENCES_TS_PREFIX}${key}`);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const setLocalPreferenceTimestamp = (originalSetItem, key, ts) => {
  try {
    originalSetItem.call(localStorage, `${PREFERENCES_TS_PREFIX}${key}`, String(ts));
  } catch {
    return;
  }
};

const getLocalSyncedPreferencesSnapshot = () => {
  const keys = new Set(Array.from(SYNCED_PREFERENCE_KEYS));

  try {
    Object.keys(localStorage).forEach((k) => {
      if (isSyncedPreferenceKey(k)) {
        keys.add(k);
      }

      if (k.startsWith(PREFERENCES_TS_PREFIX)) {
        const derivedKey = k.slice(PREFERENCES_TS_PREFIX.length);
        if (isSyncedPreferenceKey(derivedKey)) {
          keys.add(derivedKey);
        }
      }
    });
  } catch {
    // ignore
  }

  const values = {};
  const ts = {};

  keys.forEach((k) => {
    try {
      const v = localStorage.getItem(k);
      if (v !== null) {
        values[k] = v;
      }
    } catch {
      // ignore
    }

    ts[k] = getLocalPreferenceTimestamp(k);
  });

  return { values, ts };
};

const getCloudSyncedPreferencesSnapshot = (profileRow) => {
  const preferences = (profileRow && profileRow.preferences && typeof profileRow.preferences === 'object')
    ? profileRow.preferences
    : {};

  const cloudTs = (preferences.__ts && typeof preferences.__ts === 'object') ? preferences.__ts : {};
  const values = {};
  const ts = {};

  Object.keys(preferences).forEach((k) => {
    if (PREFERENCES_META_KEYS.has(k)) return;
    if (!isSyncedPreferenceKey(k)) return;

    const v = preferences[k];
    if (v === null) {
      values[k] = null;
      return;
    }

    if (v !== undefined) {
      values[k] = typeof v === 'string' ? v : JSON.stringify(v);
    }
  });

  Object.keys(cloudTs).forEach((k) => {
    if (!isSyncedPreferenceKey(k)) return;
    const n = parseInt(String(cloudTs[k] ?? '0'), 10);
    ts[k] = Number.isFinite(n) ? n : 0;
  });

  return { values, ts, rawPreferences: preferences };
};

const mergePreferencesLastChangeWins = (localSnap, cloudSnap) => {
  const keys = new Set([
    ...Object.keys(localSnap.values || {}),
    ...Object.keys(localSnap.ts || {}),
    ...Object.keys(cloudSnap.values || {}),
    ...Object.keys(cloudSnap.ts || {}),
  ]);

  const mergedValues = {};
  const mergedTs = {};

  const now = Date.now();

  keys.forEach((k) => {
    if (!isSyncedPreferenceKey(k)) return;

    const localValue = localSnap.values ? localSnap.values[k] : undefined;
    const cloudValue = cloudSnap.values ? cloudSnap.values[k] : undefined;
    const localTs = localSnap.ts ? (localSnap.ts[k] || 0) : 0;
    const cloudTs = cloudSnap.ts ? (cloudSnap.ts[k] || 0) : 0;

    if (localTs === 0 && cloudTs === 0) {
      if (cloudValue !== undefined) {
        mergedValues[k] = cloudValue;
        mergedTs[k] = now;
        return;
      }
      if (localValue !== undefined) {
        mergedValues[k] = localValue;
        mergedTs[k] = now;
      }
      return;
    }

    if (localTs >= cloudTs) {
      mergedValues[k] = localValue !== undefined ? localValue : null;
      mergedTs[k] = localTs;
      return;
    }

    mergedValues[k] = cloudValue !== undefined ? cloudValue : null;
    mergedTs[k] = cloudTs;
  });

  return { values: mergedValues, ts: mergedTs };
};

const buildNextProfilePreferences = (existingPreferences, merged) => {
  const base = (existingPreferences && typeof existingPreferences === 'object') ? existingPreferences : {};
  const nextTs = { ...(base.__ts && typeof base.__ts === 'object' ? base.__ts : {}) };

  const next = { ...base };

  Object.keys(merged.values || {}).forEach((k) => {
    if (!isSyncedPreferenceKey(k)) return;
    next[k] = merged.values[k];
  });

  Object.keys(merged.ts || {}).forEach((k) => {
    if (!isSyncedPreferenceKey(k)) return;
    nextTs[k] = merged.ts[k];
  });

  next.__ts = nextTs;
  next.__version = PREFERENCES_VERSION;
  return next;
};

const applyMergedPreferencesToLocalStorage = (merged, originalSetItem, originalRemoveItem) => {
  const keys = Object.keys(merged.values || {});
  keys.forEach((k) => {
    if (!isSyncedPreferenceKey(k)) return;
    const v = merged.values[k];
    try {
      if (v === null || v === undefined) {
        originalRemoveItem.call(localStorage, k);
      } else {
        originalSetItem.call(localStorage, k, v);
      }
    } catch {
      // ignore
    }

    if (merged.ts && merged.ts[k]) {
      setLocalPreferenceTimestamp(originalSetItem, k, merged.ts[k]);
    }
  });
};

const fetchProfileRowById = async (userId) => {
  try {
    const { data, error } = await awaitWithTimeout(
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(),
      12000,
      'profiles.select(maybeSingle)'
    );

    if (error) {
      return { data: null, error };
    }

    if (data) {
      return { data, error: null };
    }

    const { data: rows, error: rowsError } = await awaitWithTimeout(
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .limit(1),
      12000,
      'profiles.select(limit1)'
    );

    if (rowsError) {
      return { data: null, error: rowsError };
    }

    const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
    return { data: row, error: null };
  } catch (e) {
    return { data: null, error: e };
  }
};

// ============================================================================
// OPTIMISATION : Lecture optimiste du cache localStorage pour débloquer l'UI
// ============================================================================

const SUPABASE_STORAGE_KEY = () => {
  try {
    const supabaseKey = supabase?.auth?.storageKey || supabase?.auth?._storageKey;
    if (typeof supabaseKey === 'string' && supabaseKey) {
      return supabaseKey;
    }

    const url = process.env.REACT_APP_SUPABASE_URL || '';
    if (!url) return 'sb-auth-token';

    const hostname = new URL(url).hostname;
    const projectRef = hostname.split('.')[0];
    if (projectRef) {
      return `sb-${projectRef}-auth-token`;
    }

    return 'sb-auth-token';
  } catch {
    return 'sb-auth-token';
  }
};

const PROFILE_CACHE_KEY = (userId) => `mpt_profile_cache_${userId}`;

const syncMaintenanceAdminBypass = (profile) => {
  const isAdmin = profile?.role === 'admin';
  const expiresAt = Date.now() + MPT_MAINTENANCE_ADMIN_BYPASS_TTL_MS;
  const message = {
    type: 'MPT_MAINTENANCE_ADMIN_BYPASS',
    enabled: isAdmin,
    expiresAt,
  };

  try {
    if (isAdmin) {
      localStorage.setItem(MPT_MAINTENANCE_ADMIN_BYPASS_KEY, JSON.stringify({
        enabled: true,
        expiresAt,
        updatedAt: Date.now(),
      }));
    } else {
      localStorage.removeItem(MPT_MAINTENANCE_ADMIN_BYPASS_KEY);
    }
  } catch {
    // ignore storage errors
  }

  try {
    if (!('serviceWorker' in navigator)) return;

    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message);
    }

    navigator.serviceWorker.ready
      .then((registration) => {
        if (registration?.active) {
          registration.active.postMessage(message);
        }
      })
      .catch(() => {});
  } catch {
    // ignore Service Worker messaging errors
  }
};

/**
 * Lit la session depuis le cookie de Supabase de manière synchrone.
 * Permet de débloquer l'UI instantanément sans attendre getSession().
 */
const getCachedSessionFromStorage = () => {
  try {
    const storageKey = SUPABASE_STORAGE_KEY();
    // Lire depuis le cookie storage adapter (source de vérité)
    const json = cookieStorageAdapter.getItem(storageKey);
    if (!json) return null;

    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    
    // Vérifie si le token n'est pas expiré côté client (avec 60s de marge)
    const expiresAt = parsed.expires_at;
    if (expiresAt && expiresAt * 1000 < Date.now() + 60000) {
      return null;
    }

    // Reconstruit un objet session compatible
    return {
      access_token: parsed.access_token,
      refresh_token: parsed.refresh_token,
      expires_at: parsed.expires_at,
      expires_in: parsed.expires_in,
      token_type: parsed.token_type || 'bearer',
      user: parsed.user,
    };
  } catch (e) {
    return null;
  }
};

/**
 * Sauvegarde le profil dans le localStorage pour cache.
 */
const cacheProfileToStorage = (userId, profile) => {
  try {
    if (!userId || !profile) return;
    localStorage.setItem(PROFILE_CACHE_KEY(userId), JSON.stringify({
      profile,
      timestamp: Date.now(),
    }));
  } catch {
    // ignore
  }
};

/**
 * Lit le profil depuis le cache localStorage.
 */
const getCachedProfileFromStorage = (userId) => {
  try {
    if (!userId) return null;
    const json = localStorage.getItem(PROFILE_CACHE_KEY(userId));
    if (!json) return null;
    
    const parsed = JSON.parse(json);
    // Cache valide pendant 24h
    if (Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(PROFILE_CACHE_KEY(userId));
      return null;
    }
    return parsed.profile;
  } catch {
    return null;
  }
};

export const AuthContext = createContext({
  user: null,
  session: null,
  profile: null,
  loading: true,
  authActionLoading: false,
  runWithAuthUiLoader: async (operation) => operation(),
  signUp: async () => {},
  signIn: async () => {},
  sendMagicLink: async () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
  refreshBilling: async () => {},
  billing: null,
});

const isAuthCallbackLocation = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.location.pathname.endsWith('/auth/callback');
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authActionLoading, setAuthActionLoading] = useState(false);

  const userRef = useRef(null);
  const profileRef = useRef(null);
  const localStorageInterceptorInstalledRef = useRef(false);
  const localStorageOriginalMethodsRef = useRef(null);
  const pendingPreferencesFlushTimerRef = useRef(null);
  const pendingPreferenceKeysRef = useRef(new Set());
  const authActionTimerRef = useRef(null);
  const authActionInFlightCountRef = useRef(0);

  const startAuthActionLoader = useCallback(() => {
    authActionInFlightCountRef.current += 1;

    if (authActionInFlightCountRef.current > 1) {
      return;
    }

    if (authActionTimerRef.current) {
      clearTimeout(authActionTimerRef.current);
      authActionTimerRef.current = null;
    }

    authActionTimerRef.current = setTimeout(() => {
      authActionTimerRef.current = null;
      if (authActionInFlightCountRef.current > 0) {
        setAuthActionLoading(true);
      }
    }, AUTH_ACTION_LOADER_DELAY_MS);
  }, []);

  const stopAuthActionLoader = useCallback(() => {
    authActionInFlightCountRef.current = Math.max(0, authActionInFlightCountRef.current - 1);

    if (authActionInFlightCountRef.current > 0) {
      return;
    }

    if (authActionTimerRef.current) {
      clearTimeout(authActionTimerRef.current);
      authActionTimerRef.current = null;
    }

    setAuthActionLoading(false);
  }, []);

  const runWithAuthUiLoader = useCallback(async (operation, options = {}) => {
    const { trackUi = true } = options;

    if (!trackUi) {
      return operation();
    }

    startAuthActionLoader();
    try {
      return await operation();
    } finally {
      stopAuthActionLoader();
    }
  }, [startAuthActionLoader, stopAuthActionLoader]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    profileRef.current = profile;
    syncMaintenanceAdminBypass(profile);
  }, [profile]);

  // Charger la session et le profil au démarrage
  useEffect(() => {
    let isMounted = true;

    // Timeout de sécurité : si getSession() hang, on débloque quand même le rendu après 2s
    // Réduit de 5s à 2s car le cache local débloque déjà l'UI instantanément
    const safetyTimeoutId = setTimeout(() => {
      if (!isMounted) return;
      setLoading((prev) => {
        if (!prev) return prev;
        return false;
      });
    }, 2000);

    const flushPendingPreferenceChangesToSupabase = async () => {
      const currentUser = userRef.current;
      if (!currentUser?.id) return;

      const changedKeys = Array.from(pendingPreferenceKeysRef.current);
      pendingPreferenceKeysRef.current = new Set();

      if (changedKeys.length === 0) return;

      const basePreferences = profileRef.current?.preferences && typeof profileRef.current.preferences === 'object'
        ? profileRef.current.preferences
        : {};

      const nextPreferences = buildNextProfilePreferences(basePreferences, {
        values: changedKeys.reduce((acc, k) => {
          try {
            const v = localStorage.getItem(k);
            acc[k] = v !== null ? v : null;
          } catch {
            // ignore
          }
          return acc;
        }, {}),
        ts: changedKeys.reduce((acc, k) => {
          acc[k] = getLocalPreferenceTimestamp(k) || Date.now();
          return acc;
        }, {}),
      });

      try {
        const { error } = await supabase
          .from('profiles')
          .update({ preferences: nextPreferences })
          .eq('id', currentUser.id);

        if (error) {
          return;
        }

        setProfile((prev) => (prev ? { ...prev, preferences: nextPreferences } : prev));
      } catch (e) {
        // ignore preference sync errors (non-blocking)
      }
    };

    const schedulePreferenceFlush = () => {
      if (pendingPreferencesFlushTimerRef.current) {
        clearTimeout(pendingPreferencesFlushTimerRef.current);
      }
      pendingPreferencesFlushTimerRef.current = setTimeout(() => {
        pendingPreferencesFlushTimerRef.current = null;
        flushPendingPreferenceChangesToSupabase();
      }, 750);
    };

    const installLocalStorageInterceptor = () => {
      if (localStorageInterceptorInstalledRef.current) return;
      if (typeof window === 'undefined' || !window.localStorage) return;

      const originalSetItem = localStorage.setItem;
      const originalRemoveItem = localStorage.removeItem;

      localStorageOriginalMethodsRef.current = { originalSetItem, originalRemoveItem };

      localStorage.setItem = function patchedSetItem(key, value) {
        originalSetItem.call(this, key, value);

        if (!isSyncedPreferenceKey(key)) return;
        const currentUser = userRef.current;
        const now = Date.now();
        setLocalPreferenceTimestamp(originalSetItem, key, now);
        pendingPreferenceKeysRef.current.add(key);
        if (currentUser?.id) {
          schedulePreferenceFlush();
          // Sauvegarder aussi dans le cookie de préférences
          if (isPreferenceKey(key)) {
            savePreferencesToCookie();
          }
        }
      };

      localStorage.removeItem = function patchedRemoveItem(key) {
        originalRemoveItem.call(this, key);

        if (!isSyncedPreferenceKey(key)) return;
        const currentUser = userRef.current;
        const now = Date.now();
        setLocalPreferenceTimestamp(originalSetItem, key, now);
        pendingPreferenceKeysRef.current.add(key);
        if (currentUser?.id) {
          schedulePreferenceFlush();
          // Mettre à jour le cookie de préférences
          if (isPreferenceKey(key)) {
            savePreferencesToCookie();
          }
        }
      };

      localStorageInterceptorInstalledRef.current = true;
    };

    const syncPreferencesOnLogin = async (userId, profileRow, { isExplicitLogin = false } = {}) => {
      const methods = localStorageOriginalMethodsRef.current;
      const originalSetItem = methods?.originalSetItem || localStorage.setItem;
      const originalRemoveItem = methods?.originalRemoveItem || localStorage.removeItem;
      const cloudSnap = getCloudSyncedPreferencesSnapshot(profileRow);
      const hasCloudPreferences = Object.keys(cloudSnap.values || {}).length > 0;

      let merged;

      if (isExplicitLogin && hasCloudPreferences) {
        clearSyncedPreferencesFromLocalStorage(originalRemoveItem);
        merged = {
          values: { ...(cloudSnap.values || {}) },
          ts: { ...(cloudSnap.ts || {}) },
        };
      } else {
        // Refresh silencieux OU nouveau membre : fusion last-change-wins
        const cookiePrefs = readPreferencesFromCookie();
        if (cookiePrefs && isExplicitLogin) {
          restorePreferencesToLocalStorage(cookiePrefs);
        }

        const localSnap = getLocalSyncedPreferencesSnapshot();
        merged = mergePreferencesLastChangeWins(localSnap, cloudSnap);
      }

      applyMergedPreferencesToLocalStorage(merged, originalSetItem, originalRemoveItem);

      // Sauvegarder les préférences fusionnées dans le cookie
      savePreferencesToCookie();

      const nextPreferences = buildNextProfilePreferences(cloudSnap.rawPreferences, merged);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .update({ preferences: nextPreferences })
          .eq('id', userId)
          .select('*')
          .maybeSingle();

        if (error) {
          // Émettre l'événement de login même en cas d'erreur de sync
          emitAuthEvent(AUTH_EVENTS.SIGNED_IN);
          return profileRow;
        }

        // Émettre l'événement pour que l'UI se mette à jour avec les préférences restaurées
        emitAuthEvent(AUTH_EVENTS.SIGNED_IN);
        return data || { ...profileRow, preferences: nextPreferences };
      } catch (e) {
        emitAuthEvent(AUTH_EVENTS.SIGNED_IN);
        return profileRow;
      }
    };

    installLocalStorageInterceptor();

    const initializeAuth = async () => {
      
      // ============================================================================
      // ⚡ OPTIMISATION : Lecture optimiste du cache pour débloquer l'UI immédiatement
      // ============================================================================
      const cachedSession = getCachedSessionFromStorage();
      
      if (cachedSession?.user) {
        
        // Débloque l'UI instantanément avec les données du cache
        if (isMounted) {
          setSession(cachedSession);
          setUser(cachedSession.user);
          setLoading(false); // ← UI débloquée en ~10ms !
        }
        
        // Charge le profil depuis le cache aussi
        const cachedProfile = getCachedProfileFromStorage(cachedSession.user.id);
        if (cachedProfile && isMounted) {
          setProfile(cachedProfile);
        }
      }
      
      // ============================================================================
      // 🔒 VALIDATION : Vérifie la session avec le serveur en arrière-plan
      // ============================================================================
      try {
        const { data, error } = await awaitWithTimeout(
          supabase.auth.getSession(),
          8000,
          'supabase.auth.getSession'
        );
        
        if (error) {
          // En cas d'erreur serveur, garde le cache mais déconnecte quand même
          if (isMounted) {
            setSession(null);
            setUser(null);
            setProfile(null);
            setBilling(null);
            setLoading(false);
          }
          return;
        }

        const serverSession = data?.session ?? null;
        
        // Met à jour seulement si différent du cache (évite les re-renders inutiles)
        const cachedUserId = cachedSession?.user?.id;
        const serverUserId = serverSession?.user?.id;
        
        if (serverUserId !== cachedUserId) {
          if (isMounted) {
            setSession(serverSession);
            setUser(serverSession?.user ?? null);
            
            if (!serverSession?.user) {
              setProfile(null);
            }
          }
        }

        // Charge le profil si on a un utilisateur
        if (serverSession?.user) {
          loadProfile(serverSession.user.id, isMounted, { showLoader: false, isExplicitLogin: false });
        }
      } catch (e) {
        // ignore: fallback handling below
        // Fallback : si on a une session cache, tenter de recharger le profil quand même
        if (cachedSession?.user?.id) {
          try {
            loadProfile(cachedSession.user.id, isMounted, { showLoader: false, isExplicitLogin: false });
          } catch {
            // ignore
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const loadProfile = async (userId, stillMounted = true, options = {}) => {
      const { showLoader = false } = options;

      const executeLoadProfile = async () => {
        try {
          const { data, error } = await fetchProfileRowById(userId);

          if (error) {
            if (isAuthTimeoutError(error)) {
              const cached = getCachedProfileFromStorage(userId);
              if (cached && stillMounted) {
                setProfile(cached);
              }
            }
            return;
          }

          if (!data) {
            // Le trigger handle_new_user crée le profil côté serveur.
            // Si le profil n'est pas encore visible, on retente après un court délai.
            let retryProfile = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
              await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
              const { data: retryData } = await fetchProfileRowById(userId);
              if (retryData) {
                retryProfile = retryData;
                break;
              }
            }

            if (retryProfile && stillMounted) {
              const syncedRetryProfile = await syncPreferencesOnLogin(userId, retryProfile, { isExplicitLogin: true });
              if (stillMounted) {
                setProfile(syncedRetryProfile || null);
                // 💾 Sauvegarde dans le cache
                cacheProfileToStorage(userId, syncedRetryProfile);
              }
            }
            return;
          }

          const syncedProfile = await syncPreferencesOnLogin(userId, data || null, { isExplicitLogin: options.isExplicitLogin === true });

          if (stillMounted) {
            setProfile(syncedProfile || null);
            // 💾 Sauvegarde dans le cache pour les prochains chargements rapides
            cacheProfileToStorage(userId, syncedProfile);
          }
        } catch (e) {
          // ignore
        }
      };

      if (showLoader) {
        return runWithAuthUiLoader(executeLoadProfile);
      }

      return executeLoadProfile();
    };

    initializeAuth();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        const currentUser = newSession?.user ?? null;
        if (event === 'PASSWORD_RECOVERY' && isAuthCallbackLocation()) {
          rememberPendingAuthFlowType('recovery');
        }
        setSession(newSession);
        setUser(currentUser);

        if (currentUser) {
          const isExplicitLogin = event === 'SIGNED_IN';
          await loadProfile(currentUser.id, isMounted, { showLoader: false, isExplicitLogin });
        } else {
          setProfile(null);
          setBilling(null);
        }
      }
    );

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeoutId);
      subscription.subscription.unsubscribe();

      if (pendingPreferencesFlushTimerRef.current) {
        clearTimeout(pendingPreferencesFlushTimerRef.current);
        pendingPreferencesFlushTimerRef.current = null;
      }

      if (authActionTimerRef.current) {
        clearTimeout(authActionTimerRef.current);
        authActionTimerRef.current = null;
      }
      authActionInFlightCountRef.current = 0;

      if (localStorageInterceptorInstalledRef.current && localStorageOriginalMethodsRef.current) {
        try {
          localStorage.setItem = localStorageOriginalMethodsRef.current.originalSetItem;
          localStorage.removeItem = localStorageOriginalMethodsRef.current.originalRemoveItem;
        } catch {
          // ignore
        }

        localStorageInterceptorInstalledRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async ({ email, password, username, redirect }) => runWithAuthUiLoader(async () => {
    const safeRedirect = getSafeAuthRedirect(redirect);
    const redirectUrl = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(safeRedirect)}`;

    // Sauvegarder les préférences locales dans le cookie AVANT l'inscription.
    // Si l'utilisateur doit confirmer son email et revient plus tard,
    // le cookie préserve ses préférences même si le localStorage est vidé.
    savePreferencesToCookie();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          username: username || null,
        },
      },
    });

    if (error) {
      throw error;
    }

    // Le trigger handle_new_user côté serveur crée automatiquement le profil
    // dans public.profiles en lisant raw_user_meta_data.username.
    // On rafraîchit le profil si l'utilisateur est déjà confirmé.
    const newUser = data?.user;
    if (newUser && newUser.email_confirmed_at) {
      await refreshProfileInternal(newUser.id);
    }

    return data;
  });

  const sendMagicLink = async ({ email, redirect }) => runWithAuthUiLoader(async () => {
    const redirectPath = getSafeAuthRedirect(redirect);
    const emailRedirectTo = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectPath)}`;

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo,
        shouldCreateUser: false,
      },
    });

    if (error) {
      throw error;
    }

    return data;
  });

  const signIn = async ({ email, password }) => runWithAuthUiLoader(async () => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    return data;
  });

  const signOut = async () => {
    const userIdBeforeSignOut = userRef.current?.id || user?.id;

    // Déconnexion serveur en arrière-plan (ne doit pas bloquer l'UX locale)
    const remoteSignOutPromise = awaitWithTimeout(
      supabase.auth.signOut(),
      4000,
      'supabase.auth.signOut'
    ).then(({ error }) => {
      if (error) {
        // ignore background signOut error
      }
    }).catch(() => {
      // ignore background signOut rejection
    });

    if (pendingPreferencesFlushTimerRef.current) {
      clearTimeout(pendingPreferencesFlushTimerRef.current);
      pendingPreferencesFlushTimerRef.current = null;
    }
    pendingPreferenceKeysRef.current = new Set();
    userRef.current = null;
    profileRef.current = null;

    // 🍪 Nettoie le cookie de session (suppression complète)
    clearAuthCookie();

    clearPreferencesCookie();

    clearSyncedPreferencesFromLocalStorage();
    
    // 🧹 Nettoie le cache du profil lors de la déconnexion
    if (userIdBeforeSignOut) {
      try {
        localStorage.removeItem(PROFILE_CACHE_KEY(userIdBeforeSignOut));
      } catch (e) {
        // ignore
      }
    }

    syncMaintenanceAdminBypass(null);
    
    // 🔄 Réinitialise les préférences localStorage aux valeurs par défaut
    resetPreferencesToDefaults();
    
    setSession(null);
    setUser(null);
    setProfile(null);
    setBilling(null);
    
    // 📡 Émet l'événement pour que TOUTE l'UI se réinitialise
    emitAuthEvent(AUTH_EVENTS.SIGNED_OUT);

    // Laisse la promesse tourner sans bloquer l'UI
    remoteSignOutPromise.catch(() => {});
    
    // 🏠 Redirige vers l'accueil après déconnexion
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const refreshProfileInternal = useCallback(async (userId) => {
    try {
      const { data, error } = await fetchProfileRowById(userId);

      if (error) {
        return;
      }

      if (!data) {
        return;
      }

      setProfile(data || null);
      // 💾 Met à jour le cache lors du rafraîchissement
      cacheProfileToStorage(userId, data);
    } catch (e) {
      // ignore refresh errors
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await refreshProfileInternal(user.id);
    }
  }, [refreshProfileInternal, user?.id]);

  const refreshBilling = useCallback(async () => {
    if (!user?.id) {
      setBilling(null);
      return null;
    }

    try {
      const state = await getBillingState();
      setBilling(state || null);
      return state || null;
    } catch {
      setBilling(null);
      return null;
    }
  }, [user?.id]);

  useEffect(() => {
    refreshBilling();
  }, [refreshBilling]);

  const value = {
    user,
    session,
    profile,
    billing,
    loading,
    authActionLoading,
    runWithAuthUiLoader,
    signUp,
    signIn,
    sendMagicLink,
    signOut,
    refreshProfile,
    refreshBilling,
  };

  return <AuthContext.Provider value={value}>{loading ? null : children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
